import React, { useEffect, useState } from 'react';
import { FaPlusCircle, FaEdit, FaTrashAlt, FaRunning, FaBiking, FaSwimmer, FaExchangeAlt } from 'react-icons/fa';
import {
  getConfigsByTournament,
  createTournamentConfig,
  updateTournamentConfig,
  deleteTournamentConfig,
  TournamentConfig,
  TournamentConfigType,
  CreateTournamentConfigRequest
} from '../../services/tournamentConfigService';
import { isAdmin } from '../../services/authService';
import './tournamentConfigPanel.css';

interface TournamentConfigPanelProps {
  tournamentId: number;
  activityTypes: { id: number; name: string }[];
  onClose: () => void;
}

const CONFIG_TYPE_LABELS: Record<TournamentConfigType, string> = {
  MIN_DISTANCE: 'Khoảng cách tối thiểu',
  ACTIVITY_CONVERSION: 'Tỷ lệ chuyển đổi'
};

const CONFIG_TYPE_DESCRIPTIONS: Record<TournamentConfigType, string> = {
  MIN_DISTANCE: 'Khoảng cách tối thiểu (km) cho mỗi hoạt động để được tính hợp lệ',
  ACTIVITY_CONVERSION: 'Tỷ lệ chuyển đổi hoạt động khác sang tương đương chạy bộ'
};

const CONFIG_TYPE_EXAMPLES: Record<TournamentConfigType, string> = {
  MIN_DISTANCE: 'Ví dụ: 2.0 nghĩa là mỗi phiên chạy phải đạt tối thiểu 2km',
  ACTIVITY_CONVERSION: 'Ví dụ: 3.0 cho đạp xe nghĩa là 3km đạp xe = 1km chạy bộ'
};

const CONFIG_ICONS: Record<TournamentConfigType, React.ReactNode> = {
  MIN_DISTANCE: <FaRunning />,
  ACTIVITY_CONVERSION: <FaExchangeAlt />
};

const CONFIG_COLORS: Record<TournamentConfigType, string> = {
  MIN_DISTANCE: '#2196f3',
  ACTIVITY_CONVERSION: '#9c27b0'
};

type ActiveTabType = TournamentConfigType | 'ALL';

const TournamentConfigPanel: React.FC<TournamentConfigPanelProps> = ({ tournamentId, activityTypes, onClose }) => {
  const [configs, setConfigs] = useState<TournamentConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTabType>('ALL');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addData, setAddData] = useState<CreateTournamentConfigRequest>({
    tournamentId,
    configType: 'MIN_DISTANCE',
    activityTypeId: activityTypes[0]?.id || 0,
    value: ''
  });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [editConfig, setEditConfig] = useState<{id: number, value: string} | null>(null);

  const isUserAdmin = isAdmin();

  const fetchConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getConfigsByTournament(tournamentId);
      setConfigs(data);
    } catch (err: any) {
      setError(err?.message || 'Không thể tải cấu hình');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, [tournamentId]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddLoading(true);
    setAddError(null);
    try {
      await createTournamentConfig(addData);
      setShowAddForm(false);
      setAddData({ ...addData, value: '' });
      fetchConfigs();
    } catch (err: any) {
      setAddError(err?.message || 'Không thể thêm cấu hình');
    } finally {
      setAddLoading(false);
    }
  };

  const handleEdit = async (id: number, value: string) => {
    try {
      await updateTournamentConfig(id, value);
      setEditConfig(null);
      fetchConfigs();
    } catch (err: any) {
      alert(err?.message || 'Không thể cập nhật cấu hình');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa cấu hình này?')) return;
    try {
      await deleteTournamentConfig(id);
      fetchConfigs();
    } catch (err: any) {
      alert(err?.message || 'Không thể xóa cấu hình');
    }
  };

  const filteredConfigs = activeTab === 'ALL' 
    ? configs 
    : configs.filter(config => config.configType === activeTab);

  const countByType = (type: TournamentConfigType) => {
    return configs.filter(config => config.configType === type).length;
  };

  const getTabClass = (tabName: ActiveTabType) => {
    return `config-tab ${activeTab === tabName ? 'active' : ''}`;
  };

  // Hiển thị icon cho hoạt động
  const getActivityIcon = (activityTypeId: number) => {
    const activityName = activityTypes.find(a => a.id === activityTypeId)?.name?.toLowerCase() || '';
    if (activityName.includes('run') || activityName.includes('chạy')) {
      return <FaRunning className="activity-icon running" />;
    } else if (activityName.includes('bike') || activityName.includes('cycle') || activityName.includes('đạp')) {
      return <FaBiking className="activity-icon cycling" />;
    } else if (activityName.includes('swim') || activityName.includes('bơi')) {
      return <FaSwimmer className="activity-icon swimming" />;
    }
    return null;
  };

  const renderConfigCard = (config: TournamentConfig) => {
    const isEditing = editConfig?.id === config.id;
    const activityName = activityTypes.find(a => a.id === config.activityTypeId)?.name || `ID: ${config.activityTypeId}`;
    const configTypeColor = CONFIG_COLORS[config.configType];
    const configTypeIcon = CONFIG_ICONS[config.configType];
    
    return (
      <div className={`config-card ${config.configType.toLowerCase()}`} key={config.id}>
        <div className="config-card-header" style={{ borderBottom: `1px solid ${configTypeColor}20` }}>
          <div className="config-icon" style={{ color: configTypeColor }}>
            {configTypeIcon}
          </div>
          <div className="config-type">{CONFIG_TYPE_LABELS[config.configType]}</div>
          {isUserAdmin && (
            <div className="config-actions">
              {!isEditing && (
                <>
                  <button 
                    className="tournament-action-btn edit" 
                    onClick={() => setEditConfig({id: config.id, value: config.value})}
                    title="Sửa cấu hình"
                  >
                    <FaEdit />
                  </button>
                  <button 
                    className="tournament-action-btn delete" 
                    onClick={() => handleDelete(config.id)}
                    title="Xóa cấu hình"
                  >
                    <FaTrashAlt />
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="config-card-body">
          <div className="config-activity">
            <span className="label">Hoạt động:</span>
            <div className="value activity-value">
              {getActivityIcon(config.activityTypeId)}
              {activityName}
            </div>
          </div>
          
          {isEditing ? (
            <div className="config-edit">
              <input 
                type="text" 
                value={editConfig.value}
                onChange={e => setEditConfig({...editConfig, value: e.target.value})}
                className="edit-input"
                autoFocus
              />
              <div className="edit-actions">
                <button 
                  className="edit-save"
                  onClick={() => handleEdit(config.id, editConfig.value)}
                >
                  Lưu
                </button>
                <button 
                  className="edit-cancel"
                  onClick={() => setEditConfig(null)}
                >
                  Hủy
                </button>
              </div>
            </div>
          ) : (
            <div className="config-value-wrapper">
              <span className="label">Giá trị:</span>
              <span className="config-value">{config.value}</span>
            </div>
          )}
          
          <div className="config-description">
            {CONFIG_TYPE_DESCRIPTIONS[config.configType]}
          </div>
        </div>
      </div>
    );
  };

  const renderAddForm = () => {
    if (!showAddForm) return null;

    return (
      <div className="add-config-container">
        <h3 className="add-config-title">Thêm cấu hình mới</h3>
        <form onSubmit={handleAdd} className="add-config-form">
          <div className="form-group">
            <label htmlFor="configType">Loại cấu hình</label>
            <select 
              id="configType"
              value={addData.configType} 
              onChange={e => setAddData({ ...addData, configType: e.target.value as TournamentConfigType })}
            >
              <option value="MIN_DISTANCE">Khoảng cách tối thiểu</option>
              <option value="ACTIVITY_CONVERSION">Tỷ lệ chuyển đổi</option>
            </select>
            <small className="form-help">{CONFIG_TYPE_DESCRIPTIONS[addData.configType]}</small>
          </div>
          
          <div className="form-group">
            <label htmlFor="activityType">Loại hoạt động</label>
            <select 
              id="activityType"
              value={addData.activityTypeId} 
              onChange={e => setAddData({ ...addData, activityTypeId: Number(e.target.value) })}
            >
              {activityTypes.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="configValue">Giá trị</label>
            <input 
              id="configValue"
              type="text"
              value={addData.value} 
              onChange={e => setAddData({ ...addData, value: e.target.value })} 
              placeholder={CONFIG_TYPE_EXAMPLES[addData.configType]}
              required 
            />
            <small className="form-help">{CONFIG_TYPE_EXAMPLES[addData.configType]}</small>
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="btn-cancel"
              onClick={() => setShowAddForm(false)}
            >
              Hủy
            </button>
            <button 
              type="submit" 
              className="btn-submit"
              disabled={addLoading}
            >
              {addLoading ? 'Đang thêm...' : 'Thêm cấu hình'}
            </button>
          </div>
          
          {addError && <div className="add-error">{addError}</div>}
        </form>
      </div>
    );
  };

  return (
    <div className="config-overlay" onClick={onClose}>
      <div className="config-container" onClick={e => e.stopPropagation()}>
        <div className="config-header">
          <h2 className="config-title">
            <span className="config-title-icon">⚙️</span>
            Cấu hình giải đấu
          </h2>
          <button className="config-close" onClick={onClose}>×</button>
        </div>
        
        <div className="config-content">
          <div className="config-introduction">
            <p>Cấu hình giải đấu giúp bạn kiểm soát các quy tắc hoạt động và tính điểm giải đấu.</p>
          </div>
          
          <div className="config-tabs">
            <button 
              className={getTabClass('ALL')}
              onClick={() => setActiveTab('ALL')}
            >
              Tất cả <span className="tab-count">{configs.length}</span>
            </button>
            <button 
              className={getTabClass('MIN_DISTANCE')}
              onClick={() => setActiveTab('MIN_DISTANCE')}
            >
              <span className="tab-icon">{CONFIG_ICONS.MIN_DISTANCE}</span>
              Khoảng cách tối thiểu
              <span className="tab-count">{countByType('MIN_DISTANCE')}</span>
            </button>
            <button 
              className={getTabClass('ACTIVITY_CONVERSION')}
              onClick={() => setActiveTab('ACTIVITY_CONVERSION')}
            >
              <span className="tab-icon">{CONFIG_ICONS.ACTIVITY_CONVERSION}</span>
              Tỷ lệ chuyển đổi
              <span className="tab-count">{countByType('ACTIVITY_CONVERSION')}</span>
            </button>
          </div>
          
          {loading ? (
            <div className="config-loading">Đang tải cấu hình...</div>
          ) : error ? (
            <div className="config-error">{error}</div>
          ) : (
            <>
              <div className="config-list">
                {filteredConfigs.length === 0 ? (
                  <div className="empty-configs">
                    <div className="empty-icon">📝</div>
                    <p className="empty-title">Chưa có cấu hình nào</p>
                    <p className="empty-desc">
                      {activeTab === 'ALL' 
                        ? 'Giải đấu này chưa có cấu hình nào. Hãy thêm cấu hình mới để quản lý giải đấu hiệu quả hơn.' 
                        : `Chưa có cấu hình ${CONFIG_TYPE_LABELS[activeTab].toLowerCase()} nào được thiết lập.`}
                    </p>
                  </div>
                ) : (
                  <div className="config-cards">
                    {filteredConfigs.map(renderConfigCard)}
                  </div>
                )}
              </div>
              
              {isUserAdmin && !showAddForm && (
                <div className="add-config-wrapper">
                  <button 
                    onClick={() => setShowAddForm(true)} 
                    className="add-config-btn"
                  >
                    <FaPlusCircle className="add-icon" />
                    Thêm cấu hình mới
                  </button>
                </div>
              )}
              
              {renderAddForm()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TournamentConfigPanel; 